import { ErreurApi, type Client } from '../../api/client.js';
import { envoyerPiece, PREFIXE_SESSION_PIECE } from './envoi-piece.js';
import { compresserPhoto, resumeCompression, type Encodeur, type PhotoBrute } from './photo.js';
import { TEXTES_PHOTO } from './libelles.js';
import { PREFIXE_TRANSFERT, type StockageLocal, type TransportTransfert } from './transfert.js';

/** Textes de la photo de pièce (M05) : données versionnées de `libelles.ts`. */
export const MESSAGES_PHOTO = TEXTES_PHOTO.textes;

/** Clé de la photo gardée sur l'appareil (une seule photo en attente par pièce attendue). */
export const PREFIXE_PIECE_EN_ATTENTE = 'piece-en-attente:';

export type PhotoNommee = PhotoBrute & { nom: string };

export type PhasePhoto = 'attente' | 'preparation' | 'apercu' | 'envoi' | 'attente_reseau' | 'termine' | 'erreur';

export interface EtatPhoto {
  phase: PhasePhoto;
  avant?: number;
  apres?: number;
  resume?: string;
  /** Adresse de la photo compressée (objet Blob), pour l'aperçu avant l'envoi. */
  apercuUrl?: string;
  /** Part envoyée, de 0 à 1. */
  progres?: number;
  message?: string;
}

export interface OptionsControleurPhoto {
  client: Client;
  /** Fabrique le client de l'envoi à partir de sa clé : elle devient l'Idempotency-Key de la session (voir `clientAvecCle`). */
  clientPourCle?: (cle: string) => Client;
  /** Pièce attendue que l'héritier envoie (`DocumentRequirement.id`) : le portail ne désigne rien d'autre. */
  requirementId: string;
  stockage: StockageLocal;
  fabriquerTransport: (uploadUrl: string) => TransportTransfert;
  encodeur: Encodeur;
  genererCle: () => string;
  maintenant?: () => Date;
  tailleMorceau?: number;
  /** Source de l'événement `online` ; par défaut, la fenêtre du navigateur. */
  evenements?: EventTarget;
  /** Adresse d'aperçu de la photo compressée ; par défaut, un objet Blob. */
  fabriquerUrlApercu?: (octets: Uint8Array, mime: string) => string;
  revoquerUrlApercu?: (url: string) => void;
}

export interface ControleurPhoto {
  etat(): EtatPhoto;
  abonner(ecouteur: () => void): () => void;
  /** Se résout quand la photo gardée sur l'appareil a été retrouvée (et, si un envoi était en cours, reprise). */
  readonly restauree: Promise<void>;
  /**
   * Prépare la photo (ou la promesse de sa lecture) : ne rejette jamais. Une photo illisible (lecture ou décodage
   * impossible : HEIC sur Android, fichier abîmé, fichier qui n'est pas une image) passe en erreur, avec « Reprendre ».
   */
  choisir(photo: PhotoNommee | Promise<PhotoNommee>): Promise<void>;
  /** Abandonne la photo (aperçu ou erreur) et revient au cadre ; l'état change tout de suite, le nettoyage de l'appareil suit. */
  reprendre(): Promise<void>;
  envoyer(): Promise<void>;
  /** Relance l'envoi mis en attente. Appelé par l'événement `online` et à la réouverture de la page. */
  reseauRevenu(): Promise<void>;
  /** Cesse d'écouter le réseau et libère l'aperçu (à appeler quand l'écran se ferme). */
  liberer(): void;
}

/** Ce qui est gardé sur l'appareil dès le choix de la photo : les octets compressés et tout ce qu'il faut pour reprendre. */
interface PieceGardee {
  nom: string;
  mime: string;
  octets: Uint8Array;
  cle: string;
  avant: number;
  apres: number;
  resume: string;
  /** Vrai dès que la personne a touché « Envoyer » : à la réouverture, l'envoi repart tout seul. */
  envoiLance: boolean;
}

const urlParDefaut = (octets: Uint8Array, mime: string): string => URL.createObjectURL(new Blob([new Uint8Array(octets)], { type: mime }));
const revoquerParDefaut = (url: string): void => URL.revokeObjectURL(url);

export function creerControleurPhoto(options: OptionsControleurPhoto): ControleurPhoto {
  const { stockage, requirementId } = options;
  const cleGardee = `${PREFIXE_PIECE_EN_ATTENTE}${requirementId}`;
  const fabriquerUrl = options.fabriquerUrlApercu ?? urlParDefaut;
  const revoquer = options.revoquerUrlApercu ?? revoquerParDefaut;
  const evenements: EventTarget | undefined = options.evenements ?? (typeof window === 'undefined' ? undefined : window);

  let courant: EtatPhoto = { phase: 'attente' };
  let pret: PieceGardee | null = null;
  let urlApercu: string | undefined;
  const ecouteurs = new Set<() => void>();
  const poser = (etat: EtatPhoto): void => {
    courant = etat;
    for (const e of [...ecouteurs]) e();
  };

  const libererApercu = (): void => {
    if (urlApercu !== undefined) revoquer(urlApercu);
    urlApercu = undefined;
  };
  /** Garder la photo sur l'appareil est un plus : si l'appareil refuse (espace saturé), l'envoi continue depuis la mémoire de la page. */
  const garder = async (piece: PieceGardee): Promise<void> => {
    try {
      await stockage.ecrire(cleGardee, piece);
    } catch {
      // Photo non gardée : elle partira quand même tant que la page reste ouverte.
    }
  };
  const oublier = async (piece: PieceGardee | null): Promise<void> => {
    pret = null;
    libererApercu();
    await stockage.supprimer(cleGardee);
    if (piece !== null) {
      await stockage.supprimer(`${PREFIXE_SESSION_PIECE}${piece.cle}`);
      await stockage.supprimer(`${PREFIXE_TRANSFERT}${piece.cle}`);
    }
  };
  const afficherApercu = (piece: PieceGardee): void => {
    urlApercu = fabriquerUrl(piece.octets, piece.mime);
    poser({ phase: 'apercu', avant: piece.avant, apres: piece.apres, resume: piece.resume, apercuUrl: urlApercu });
  };

  const lancerEnvoi = async (): Promise<void> => {
    if (pret === null) return;
    const piece = pret;
    poser({ ...courant, phase: 'envoi', progres: 0 });
    if (!piece.envoiLance) {
      piece.envoiLance = true;
      await garder(piece);
    }
    try {
      const bilan = await envoyerPiece({
        client: options.client,
        ...(options.clientPourCle === undefined ? {} : { clientPourCle: options.clientPourCle }),
        requirementId,
        cle: piece.cle,
        fichier: { nom: piece.nom, mime: piece.mime, octets: piece.octets },
        stockage,
        fabriquerTransport: options.fabriquerTransport,
        ...(options.tailleMorceau === undefined ? {} : { tailleMorceau: options.tailleMorceau }),
        ...(options.maintenant === undefined ? {} : { maintenant: options.maintenant }),
        surProgres: (acquis, total) => poser({ ...courant, phase: 'envoi', progres: acquis / total }),
      });
      if (bilan.etat === 'termine') {
        await oublier(piece);
        poser({ phase: 'termine', message: MESSAGES_PHOTO.termine });
      } else if (bilan.etat === 'interrompu') {
        poser({ ...courant, phase: 'attente_reseau', message: MESSAGES_PHOTO.attenteReseau });
      } else if (bilan.etat === 'echec') {
        // Panne du serveur : dite telle quelle, sans promesse de reprise au retour du réseau (S2 du 27/10).
        await oublier(piece);
        poser({ phase: 'erreur', message: MESSAGES_PHOTO.inattendu });
      } else if (bilan.etat === 'trop_lourd') {
        await oublier(piece);
        poser({ phase: 'erreur', message: MESSAGES_PHOTO.tropLourde });
      } else {
        await oublier(piece);
        poser({ phase: 'erreur', message: MESSAGES_PHOTO.corrompu });
      }
    } catch (erreur) {
      // Refus du serveur (droits, validation, limite) ou panne inattendue : jamais bloqué sur « Envoi en cours ».
      await oublier(piece);
      poser({ phase: 'erreur', message: erreur instanceof ErreurApi ? erreur.messageUtilisateur : MESSAGES_PHOTO.inattendu });
    }
  };

  const reseauRevenu = async (): Promise<void> => {
    if (courant.phase !== 'attente_reseau') return;
    await lancerEnvoi();
  };

  const surRetourReseau = (): void => {
    void reseauRevenu();
  };
  evenements?.addEventListener('online', surRetourReseau);

  // Réouverture de la page : la photo gardée sur l'appareil est retrouvée ; si la personne avait touché « Envoyer », l'envoi repart.
  const restauree = (async (): Promise<void> => {
    const gardee = await stockage.lire<PieceGardee>(cleGardee);
    if (gardee === undefined || courant.phase !== 'attente') return;
    pret = gardee;
    if (!gardee.envoiLance) {
      afficherApercu(gardee);
      return;
    }
    urlApercu = fabriquerUrl(gardee.octets, gardee.mime);
    poser({
      phase: 'attente_reseau',
      avant: gardee.avant,
      apres: gardee.apres,
      resume: gardee.resume,
      apercuUrl: urlApercu,
      message: MESSAGES_PHOTO.attenteReseau,
    });
    await reseauRevenu();
  })();

  return {
    etat: () => ({ ...courant }),
    abonner(ecouteur) {
      ecouteurs.add(ecouteur);
      return () => ecouteurs.delete(ecouteur);
    },
    restauree,
    async choisir(source) {
      poser({ phase: 'preparation' });
      let photo: PhotoNommee;
      let compressee: Awaited<ReturnType<typeof compresserPhoto>>;
      try {
        photo = await source;
        compressee = await compresserPhoto(photo, options.encodeur);
      } catch {
        await oublier(null);
        poser({ phase: 'erreur', message: MESSAGES_PHOTO.illisible });
        return;
      }
      if (!compressee.reussi) {
        await oublier(null);
        poser({ phase: 'erreur', message: MESSAGES_PHOTO.tropLourde });
        return;
      }
      libererApercu();
      const piece: PieceGardee = {
        nom: photo.nom,
        mime: photo.mime,
        octets: compressee.octets,
        cle: options.genererCle(),
        avant: compressee.avant,
        apres: compressee.apres,
        resume: resumeCompression(compressee),
        envoiLance: false,
      };
      pret = piece;
      await garder(piece);
      afficherApercu(piece);
    },
    async reprendre() {
      const piece = pret;
      libererApercu();
      pret = null;
      poser({ phase: 'attente' });
      await oublier(piece);
    },
    async envoyer() {
      if (courant.phase !== 'apercu') return;
      await lancerEnvoi();
    },
    reseauRevenu,
    liberer() {
      evenements?.removeEventListener('online', surRetourReseau);
      libererApercu();
    },
  };
}
